import { Component } from 'react';
import { connect } from 'react-redux';
import api from 'api';
import { actions } from 'store';
import { getUserPermissions } from 'shared-helpers/users';
import { Alert, ModalButtonWithForm } from 'components/common';
import { Checkbox, FormGroup, Select } from 'components/common/form';
import { AdminsTransferRightsDisplayAssets } from '.';

const AdminsTransferRights = connect((state) => ({
  featureFlags: state.serverConfig.featureFlags,
  messages: state.messages
}))(
  class extends Component {
    constructor(props) {
      super(props);

      const { targetUser } = props;

      this.adminPermissions = !targetUser.roles.includes('superadmin')
        ? new Set([...targetUser.permissions])
        : new Set();
    }

    componentWillUnmount() {
      clearTimeout(this.getUsersTimeout);
    }

    getNewAdminOptions = (value) => {
      const { targetUser } = this.props;
      clearTimeout(this.getUsersTimeout);
      return new Promise((resolve) => {
        this.getUsersTimeout = setTimeout(async () => {
          try {
            const response = await api.get(
              `/admin/users` + `?search=${value}` + `&id=ne|${targetUser.id}`
            );
            if (response.data && response.data.success) {
              const users = response.data.users
                .map((user) => ({
                  value: user,
                  label: user.email || user.username
                }))
                .filter((user) => user.value.id !== 1);
              resolve(users);
            } else {
              resolve([]);
            }
          } catch (err) {
            resolve([]);
          }
        }, 500);
      });
    };

    transferRights = async (values) => {
      const { dispatch, messages, targetUser, updateAdmins } = this.props;

      await api.post(`/admin/users/${targetUser.id}/transfer-admin-rights`, {
        newAdminId: values.newAdminId.id,
        ...(values.permissions && !targetUser.roles.includes('superadmin')
          ? {
              permissions: Object.keys(values.permissions).filter(
                (permission) => values.permissions[permission]
              )
            }
          : {})
      });

      dispatch(
        actions.addNotice({
          text: messages.usersTransferAdminRightsSuccess,
          type: 'success'
        })
      );

      await updateAdmins({ showLoading: false });

      // we return false because the container modal is destroyed and we
      // don't want to call any internal functions afterwards
      return false;
    };

    validateNewAdmin = (value) => {
      if (value) this.adminPermissions.add(...value.permissions);
      else return this.props.messages.settingsAddAdminUserError;
    };

    render() {
      const {
        featureFlags,
        isInDropdown = true,
        messages,
        targetUser
      } = this.props;

      const userPermissions = getUserPermissions({ messages });
      if (!featureFlags.enableCompetencyImprovements) {
        delete userPermissions['manage-competencies'];
        delete userPermissions['manage-competency-profiles'];
        delete userPermissions['grant-competencies'];
      }

      return (
        <ModalButtonWithForm
          buttonIcon="arrow-circle-right"
          buttonText={messages.usersTransferAdminRights}
          modalActionText={messages.commonTransfer}
          modalTitle={messages.usersTransferAdminRights}
          onSubmit={this.transferRights}
          pristineIsValid={false}
          {...(isInDropdown ? { buttonIsDropdown: true } : {})}
        >
          {(formState) => (
            <div className="content">
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 15 }}
              >
                <div className="description">
                  {messages.usersTransferAdminRightsDescription}
                </div>

                {targetUser.isSupervisor ? (
                  <Alert
                    type="warning-light"
                    text={messages.usersTransferAdminRightsSupervisors}
                  />
                ) : null}

                <AdminsTransferRightsDisplayAssets
                  assets={targetUser.administeredContent}
                />

                {!targetUser.roles.includes('superadmin') &&
                formState.values?.newAdminId &&
                !formState.values?.newAdminId?.roles?.includes('superadmin') ? (
                  <FormGroup label={messages.usersPermissions}>
                    {Object.keys(userPermissions).map((permissionKey, i) => (
                      <Checkbox
                        key={permissionKey}
                        field={`permissions['${permissionKey}']`}
                        id={`permissions-${permissionKey}-${targetUser?.id}`}
                        initialValue={this.adminPermissions.has(permissionKey)}
                        label={userPermissions[permissionKey]?.label}
                        labelTooltip={userPermissions[permissionKey]?.helpText}
                        stopPropagation={true}
                        style={{ marginTop: i === 0 ? 0 : 5 }}
                      />
                    ))}
                  </FormGroup>
                ) : null}

                <FormGroup
                  htmlFor="new-admin-id"
                  label={messages.usersNewAdmin}
                >
                  <Select
                    async={true}
                    error={formState.errors.userId}
                    field="newAdminId"
                    id={`new-admin-id-${targetUser.id}`}
                    isClearable={true}
                    isSearchable={true}
                    loadingMessage={messages.settingsSearching}
                    loadOptions={this.getNewAdminOptions}
                    noOptionsMessage={messages.settingsNoUsersFound}
                    placeholder={messages.settingsSearchForUser}
                    validate={this.validateNewAdmin}
                    validateOnChange={true}
                  />
                </FormGroup>
              </div>
            </div>
          )}
        </ModalButtonWithForm>
      );
    }
  }
);

export default AdminsTransferRights;
